import { useRef, useEffect } from 'react'

interface Node {
  x: number; y: number
  baseX: number; baseY: number
  vx: number; vy: number
  radius: number
  phase: number
  speed: number
}

const CONNECTION_DIST = 180
const NODE_DENSITY = 22000

export default function ParticleBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const nodesRef = useRef<Node[]>([])
  const frameRef = useRef(0)
  const timeRef = useRef(0)
  const mouseRef = useRef({ x: -1000, y: -1000 })

  useEffect(() => {
    const cvs = canvasRef.current
    if (!cvs) return
    const ctx = cvs.getContext('2d')
    if (!ctx) return

    const resize = () => {
      const dpr = window.devicePixelRatio || 1
      cvs.width = window.innerWidth * dpr
      cvs.height = window.innerHeight * dpr
      cvs.style.width = `${window.innerWidth}px`
      cvs.style.height = `${window.innerHeight}px`
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

      const w = window.innerWidth, h = window.innerHeight
      const count = Math.min(Math.floor((w * h) / NODE_DENSITY), 60)
      nodesRef.current = Array.from({ length: count }, () => {
        const x = Math.random() * w
        const y = Math.random() * h
        return {
          x, y, baseX: x, baseY: y,
          vx: (Math.random() - 0.5) * 0.15,
          vy: (Math.random() - 0.5) * 0.15,
          radius: 1.5 + Math.random() * 2,
          phase: Math.random() * Math.PI * 2,
          speed: 0.2 + Math.random() * 0.4,
        }
      })
    }

    resize()
    window.addEventListener('resize', resize)

    const onMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY }
    }
    window.addEventListener('mousemove', onMove)

    const tick = () => {
      const w = window.innerWidth, h = window.innerHeight
      const isDark = document.documentElement.classList.contains('dark')
      ctx.clearRect(0, 0, w, h)
      timeRef.current += 0.016
      const t = timeRef.current
      const nodes = nodesRef.current

      // Update node positions with gentle drift
      for (const n of nodes) {
        const drift = Math.sin(t * n.speed + n.phase) * 12
        const driftX = Math.cos(t * n.speed * 0.7 + n.phase) * 8
        n.x = n.baseX + driftX + n.vx * t * 2
        n.y = n.baseY + drift + n.vy * t * 2

        // Wrap around edges
        if (n.x < -20) n.baseX += w + 40
        if (n.x > w + 20) n.baseX -= w + 40
        if (n.y < -20) n.baseY += h + 40
        if (n.y > h + 20) n.baseY -= h + 40
      }

      // Draw connections between nearby nodes
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x
          const dy = nodes[i].y - nodes[j].y
          const dist = Math.sqrt(dx * dx + dy * dy)
          if (dist < CONNECTION_DIST) {
            const alpha = (1 - dist / CONNECTION_DIST) * (isDark ? 0.07 : 0.045)
            ctx.beginPath()
            ctx.moveTo(nodes[i].x, nodes[i].y)
            ctx.lineTo(nodes[j].x, nodes[j].y)
            ctx.strokeStyle = isDark
              ? `rgba(255,255,255,${alpha})`
              : `rgba(160,160,160,${alpha})`
            ctx.lineWidth = 0.8
            ctx.stroke()
          }
        }
      }

      // Draw nodes
      for (const n of nodes) {
        const pulse = 0.5 + Math.sin(t * 1.5 + n.phase) * 0.3
        const baseAlpha = isDark ? 0.12 : 0.08

        // Subtle glow
        const glowR = n.radius * 4
        const glow = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, glowR)
        glow.addColorStop(0, isDark
          ? `rgba(255,255,255,${baseAlpha * pulse})`
          : `rgba(160,160,160,${baseAlpha * pulse * 0.6})`)
        glow.addColorStop(1, 'rgba(0,0,0,0)')
        ctx.beginPath(); ctx.arc(n.x, n.y, glowR, 0, Math.PI * 2)
        ctx.fillStyle = glow; ctx.fill()

        // Node dot
        ctx.beginPath(); ctx.arc(n.x, n.y, n.radius * (0.7 + pulse * 0.3), 0, Math.PI * 2)
        ctx.fillStyle = isDark
          ? `rgba(255,255,255,${0.15 + pulse * 0.1})`
          : `rgba(140,140,140,${0.12 + pulse * 0.08})`
        ctx.fill()

        // Accent highlight on ~15% of nodes
        if (n.phase > Math.PI * 1.7) {
          ctx.beginPath(); ctx.arc(n.x, n.y, n.radius * 0.6, 0, Math.PI * 2)
          ctx.fillStyle = `rgba(255,107,0,${0.08 + pulse * 0.06})`
          ctx.fill()
        }
      }

      // Mouse proximity glow
      const mx = mouseRef.current.x, my = mouseRef.current.y
      if (mx > 0 && my > 0) {
        for (const n of nodes) {
          const dx = n.x - mx, dy = n.y - my
          const dist = Math.sqrt(dx * dx + dy * dy)
          if (dist < 120) {
            const alpha = (1 - dist / 120) * 0.06
            ctx.beginPath(); ctx.arc(n.x, n.y, n.radius * 2, 0, Math.PI * 2)
            ctx.fillStyle = `rgba(255,107,0,${alpha})`
            ctx.fill()
          }
        }
      }

      frameRef.current = requestAnimationFrame(tick)
    }

    frameRef.current = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(frameRef.current)
      window.removeEventListener('resize', resize)
      window.removeEventListener('mousemove', onMove)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
    />
  )
}
